import {Input, InputNumber, message, Modal, ModalProps, Skeleton} from 'antd';
import React from 'react';
import Style from './index.module.scss';
import {
    getPlansDetail,
    PlansType,
    savePlansDetail,
    SavePlansProps,
} from '../../../service';
import useRequest from '../../../../../../Utils/Hooks/useRequest';
import {useDict} from '../../../dict';
import useRequestFn from '../../../../../../Utils/Hooks/useRequestFn';
import {errorMessage} from '../../../../../../Utils/common';
import SelectDict from '../../../../../../Features/Select/SelectDict';
import {
    modelStoreWithCode,
    ServiceScopeOptions,
} from '../../../../../../Stores/commonStore';
import {useAtom} from 'jotai';
import ProForm from '../../../../../../Components/ProForm';
import {useForm, useWatch} from 'antd/es/form/Form';

interface Props extends ModalProps {
    id?: string;
    onOk: () => void;
}

const Index = ({id, onOk, ...props}: Props) => {
    const dict = useDict();
    const [form] = useForm();

    const type = useWatch('type', form);

    const [, {loading: getLoading}] = useRequest(async () => {
        if (!id) return;
        const data = await getPlansDetail(id);
        form.setFieldsValue(data);
    });

    const [save, {loading: saveLoading}] = useRequestFn(savePlansDetail);

    const [, reload] = useAtom(
        modelStoreWithCode({
            code: 'serviceVersion',
            bindValue: 'id',
            bindName: 'name',
        }),
    );

    async function onFinish(formData: SavePlansProps) {
        try {
            await save(formData);
            await reload();
            message.success('保存成功!');
            onOk();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    function getMaxUserNumberLabel() {
        if (type === PlansType.Sms) return '容量';
        if (type === PlansType.Storage) return '容量';
        return '用户上限';
    }

    const unitMap: Record<string, string> = {
        [PlansType.Sms]: '条',
        [PlansType.Storage]: 'GB',
    };

    const [serviceArrayOptions] = useRequest(ServiceScopeOptions);

    return (
        <Modal
            {...props}
            width={800}
            title={`${id ? `修改` : '新增'}方案`}
            confirmLoading={getLoading || saveLoading}
            onOk={form.submit}>
            <div className={Style.root}>
                <Skeleton paragraph={{rows: 8}} active loading={getLoading}>
                    <ProForm form={form} onFinish={onFinish} columns={1}>
                        <ProForm.Field name="id" hidden>
                            <Input />
                        </ProForm.Field>

                        <ProForm.Field
                            label="方案类型"
                            name="type"
                            rules={[{required: true}]}
                            initialValue={dict.ScenarioType.firstValue}>
                            <SelectDict
                                options={dict.ScenarioType.data}
                                onChange={type => {
                                    form.setFieldValue(
                                        'name',
                                        dict.ScenarioType.getName(type),
                                    );
                                }}
                            />
                        </ProForm.Field>

                        <ProForm.Field
                            label="方案名称"
                            name="name"
                            rules={[{required: true}]}>
                            <Input
                                placeholder="请输入"
                                disabled={[
                                    PlansType.Expansion,
                                    PlansType.BigData,
                                    PlansType.Sms,
                                    PlansType.Storage,
                                ].includes(type)}
                            />
                        </ProForm.Field>

                        {![
                            PlansType.Expansion,
                            PlansType.BigData,
                            PlansType.Sms,
                            PlansType.Storage,
                        ].includes(type) && (
                            <ProForm.Field
                                label="服务范围"
                                name="serviceArray"
                                rules={[{required: true}]}>
                                <SelectDict
                                    options={serviceArrayOptions?.data}
                                    mode="multiple"
                                />
                            </ProForm.Field>
                        )}

                        {![
                            PlansType.Expansion,
                            PlansType.Sms,
                            PlansType.Storage,
                        ].includes(type) && (
                            <ProForm.Field
                                label="服务期限"
                                name="expireType"
                                rules={[{required: true}]}>
                                <SelectDict options={dict.ServiceTime.data} />
                            </ProForm.Field>
                        )}

                        <ProForm.Field
                            label={getMaxUserNumberLabel()}
                            name="maxUserNumber"
                            initialValue={0}>
                            <InputNumber
                                precision={0}
                                min={0}
                                max={50000}
                                suffix={unitMap[type] || '人'}
                            />
                        </ProForm.Field>

                        <ProForm.Field label="方案说明" name="remark">
                            <Input.TextArea />
                        </ProForm.Field>
                    </ProForm>
                </Skeleton>
            </div>
        </Modal>
    );
};

export default Index;
